/**
 * Pulse synthesis — the generator (pure data + logic, no JSX).
 *
 * Turns a `pulse.aggregate` response into a short editorial READ of the wall: a
 * few sentences that name the overall trajectory, what's carrying the number,
 * what's dragging it, and the single most actionable signal. Phrases that point
 * at real bands/touches are emitted as `insight` segments — each carries the
 * refs it's about (a band `key` / touch `id`, for highlighting) + a `chat` seed
 * (the question that opens the conversation). The block (`pulse-synthesis.tsx`)
 * renders these; tests assert the prose against fixtures.
 *
 * Honesty rule (mirrors the wall's "honest ghosts"): every clause is grounded in
 * a real field — `health` vs `target`, `atRisk`, `trend`, `suggestions`. Nothing
 * is invented. If nothing is instrumented, there is no read (returns null).
 *
 * This is deliberately a deterministic templated read so it's instant + free on
 * every page load. The segment output is the contract — an LLM read can replace
 * the body later without changing the block.
 */

import type {
  PulseGroupDTO,
  PulseTouchDTO,
  PulseSynthesisBrief,
} from "../../api/contracts/pulse";
import { band, type Band } from "./pulse-wall-shared";

/** One run of the read: plain text, or an interactive insight bound to the wall. */
export type SynthSeg =
  | { t: "text"; text: string }
  | { t: "insight"; text: string; tone: Band; refs: string[]; chat: string };

export type Synthesis = { segments: SynthSeg[]; pageChat: string } | null;

type LiveGroup = PulseGroupDTO & { health: number };
type LiveTouch = PulseTouchDTO & { health: number };

const isLiveGroup = (g: PulseGroupDTO): g is LiveGroup => g.health != null;
const isLiveTouch = (t: PulseTouchDTO): t is LiveTouch => t.health != null;

/** Join a list of names with commas + a trailing "and". */
function joinNames(names: string[]): string {
  if (names.length === 0) return "";
  if (names.length === 1) return names[0]!;
  if (names.length === 2) return `${names[0]} and ${names[1]}`;
  return `${names.slice(0, -1).join(", ")}, and ${names[names.length - 1]}`;
}

const text = (s: string): SynthSeg => ({ t: "text", text: s });

/** How far below target, rounded (0 when at/above). The magnitude that makes a
 *  read land — "27 under target" hits harder than "below target". */
const belowBy = (health: number, target: number) =>
  Math.max(0, Math.round(target - health));

/** The trajectory verb from the up/down/flat mix of a touch set. `down` is the
 *  urgent one — it earns "still slipping", not a neutral "sitting at". */
function trajectory(touches: LiveTouch[]): {
  dir: "up" | "down" | "flat" | null;
  verb: string;
} {
  const trends = touches.map((t) => t.trend).filter(Boolean) as Array<
    "up" | "flat" | "down"
  >;
  const ups = trends.filter((t) => t === "up").length;
  const downs = trends.filter((t) => t === "down").length;
  const dir = !trends.length
    ? null
    : ups > downs * 1.3
      ? "up"
      : downs > ups * 1.3
        ? "down"
        : "flat";
  const verb =
    dir === "up"
      ? "climbing to"
      : dir === "down"
        ? "slipping to"
        : dir === "flat"
          ? "holding at"
          : "at";
  return { dir, verb };
}

/** The single touch furthest below its own target — the one move that matters. */
function topMove(live: LiveGroup[]): { t: LiveTouch; g: LiveGroup } | null {
  return (
    live
      .flatMap((g) => g.touches.filter(isLiveTouch).map((t) => ({ t, g })))
      .filter((x) => x.t.health < x.t.target)
      .sort((a, b) => a.t.health - a.t.target - (b.t.health - b.t.target))[0] ??
    null
  );
}

/**
 * Build the read. `subject` is the function's name as it reads in a sentence
 * (e.g. "Work", "Agents"). `focus` (a band `key`) narrows the read to ONE band —
 * a sub-page drilled into that band IS that band, so the read is about its
 * signals, not the cross-band comparison. Returns null when nothing's live.
 */
export function buildPulseSynthesis(
  groups: PulseGroupDTO[],
  overall: number,
  opts: { subject: string; focus?: string | null },
): Synthesis {
  const subject = opts.subject.trim() || "This";
  const lower = subject.toLowerCase();
  const live = groups.filter(isLiveGroup).filter((g) => g.touches.length > 0);
  if (live.length === 0) return null;

  const focused = opts.focus ? live.find((g) => g.key === opts.focus) : null;
  return focused
    ? buildBandRead(focused, lower)
    : buildModeRead(live, overall, subject, lower);
}

/**
 * The cross-band read (a mode root / aggregate page). Problem-led: name the
 * number and what's dragging it (with the gap + what's at risk), the ONE move
 * that matters (with the gap + the fix), then a one-clause reassurance. When
 * nothing's below target it flips to a calm all-clear. Every clause is grounded
 * in a real field — gap = target − health, `atRisk`, `trend`, `suggestions`.
 */
function buildModeRead(
  live: LiveGroup[],
  overall: number,
  subject: string,
  lower: string,
): Synthesis {
  const segs: SynthSeg[] = [];
  const liveTouches = live.flatMap((g) => g.touches.filter(isLiveTouch));
  const { verb } = trajectory(liveTouches);

  const sorted = [...live].sort((a, b) => a.health - b.health);
  const okGroups = sorted.filter((g) => band(g.health, 70) === "ok");
  const below = sorted.filter((g) => band(g.health, 70) !== "ok"); // worst-first
  const worst = below[0] ?? null;
  const action = topMove(live);

  // ── the number, and what's pulling it ──────────────────────────────────────
  segs.push(text(`${subject} is `));
  segs.push({
    t: "insight",
    text: `${verb} ${overall}`,
    tone: band(overall, 70),
    refs: ["all"],
    chat: `Give me the read on ${lower} — what's driving the overall ${overall}?`,
  });

  if (worst) {
    const atRisk = worst.touches.reduce(
      (n, t) => n + (isLiveTouch(t) ? t.atRisk : 0),
      0,
    );
    const stake =
      atRisk > 0
        ? `, ${atRisk} at risk`
        : below.length > 1
          ? `, one of ${below.length} bands under target`
          : "";
    segs.push(text(`, held down by `));
    segs.push({
      t: "insight",
      text: worst.label,
      tone: band(worst.health, 70),
      refs: [worst.key],
      chat: `Why is ${worst.label} dragging ${lower} down, and how do I fix it?`,
    });
    segs.push(text(` at ${worst.health} against a 70 target${stake}.`));

    // ── the one move ──────────────────────────────────────────────────────────
    if (action) {
      const gap = belowBy(action.t.health, action.t.target);
      // Suppress the at-risk count here when it already rode the worst-band
      // clause above (the move touch lives inside that band) — saying "1 at risk"
      // twice reads as a glitch, not emphasis.
      const risk =
        action.t.atRisk > 0 && action.g.key !== worst.key
          ? `, ${action.t.atRisk} at risk`
          : "";
      const sugg = action.t.suggestions?.[0];
      segs.push(text(` Move first on `));
      segs.push({
        t: "insight",
        text: action.t.label,
        tone: band(action.t.health, action.t.target),
        refs: [action.t.id],
        chat: `Walk me through ${action.t.label} — it's at ${action.t.health} against a target of ${action.t.target}. What should I do first?`,
      });
      segs.push(text(` — ${gap} under target${risk}.`));
      if (sugg) segs.push(text(` Fix: ${sugg.title}.`));
    }

    // ── the strength (one clause of reassurance) ──────────────────────────────
    if (okGroups.length > 0) {
      const best = [...okGroups].sort((a, b) => b.health - a.health).slice(0, 2);
      const labels = joinNames(best.map((g) => g.label));
      segs.push(text(" "));
      segs.push({
        t: "insight",
        text: labels,
        tone: "ok",
        refs: best.map((g) => g.key),
        chat: `${labels} look healthy — anything quietly at risk there?`,
      });
      segs.push(text(`${best.length > 1 ? " are" : " is"} carrying the rest.`));
    }
  } else {
    // ── all clear — quantify the calm: name the leaders at their numbers, then
    // the one grounded reassurance (nothing at risk / what's left to watch) and
    // the floor. A bare "every band on target" reads as filler; the magnitudes
    // make the all-clear land as hard as the warning.
    const best = [...okGroups].sort((a, b) => b.health - a.health).slice(0, 2);
    segs.push(text(` — every band on target. `));
    const leader = best[0]!;
    segs.push({
      t: "insight",
      text: leader.label,
      tone: "ok",
      refs: [leader.key],
      chat: `${leader.label} is leading at ${leader.health} — anything quietly at risk there?`,
    });
    segs.push(text(` leads at ${leader.health}`));
    if (best[1]) {
      segs.push(text(", "));
      segs.push({
        t: "insight",
        text: best[1].label,
        tone: "ok",
        refs: [best[1].key],
        chat: `${best[1].label} looks healthy — anything quietly at risk there?`,
      });
      segs.push(text(` at ${best[1].health}`));
    }
    const totalAtRisk = liveTouches.reduce((n, t) => n + t.atRisk, 0);
    segs.push(
      text(
        totalAtRisk === 0
          ? `. Nothing's at risk across ${liveTouches.length} signal${liveTouches.length === 1 ? "" : "s"}`
          : `. ${totalAtRisk} signal${totalAtRisk === 1 ? "" : "s"} still to keep honest`,
      ),
    );
    const weakest = sorted[0]!;
    if (!best.some((b) => b.key === weakest.key)) {
      segs.push(text("; hold "));
      segs.push({
        t: "insight",
        text: weakest.label,
        tone: band(weakest.health, 70),
        refs: [weakest.key],
        chat: `${weakest.label} is the floor right now — is it trending the wrong way?`,
      });
      segs.push(text(` at ${weakest.health}.`));
    } else {
      segs.push(text(", so hold the line."));
    }
  }

  return {
    segments: segs,
    pageChat: `Give me the read on ${lower} — what's the most important thing to do right now?`,
  };
}

/**
 * The band read (a sub-page drilled into one band). The page IS this band, so
 * "insight into this page" is its own signals: where the band sits, the signal
 * dragging it (gap + risk + fix), and a one-line all-else. Refs point at the
 * band's bars (`data-pulse-bar`), so the read still lights the strip wall.
 */
function buildBandRead(g: LiveGroup, lower: string): Synthesis {
  const segs: SynthSeg[] = [];
  const touches = g.touches.filter(isLiveTouch);
  const { verb } = trajectory(touches);
  const tone = band(g.health, 70);

  // ── where the band sits ─────────────────────────────────────────────────────
  segs.push({
    t: "insight",
    text: g.label,
    tone,
    refs: [g.key],
    chat: `Give me the read on ${g.label} — what's driving its ${g.health}?`,
  });
  segs.push(
    text(
      tone === "ok"
        ? ` is ${verb} ${g.health}, above target.`
        : ` is ${verb} ${g.health} against a 70 target.`,
    ),
  );

  // ── the signal dragging it ──────────────────────────────────────────────────
  const belowT = touches
    .filter((t) => t.health < t.target)
    .sort((a, b) => a.health - a.target - (b.health - b.target));
  const worstT = belowT[0] ?? null;
  if (worstT) {
    const gap = belowBy(worstT.health, worstT.target);
    const risk = worstT.atRisk > 0 ? `, ${worstT.atRisk} at risk` : "";
    const sugg = worstT.suggestions?.[0];
    segs.push(text(belowT.length > 1 ? " The biggest drag is " : " The drag is "));
    segs.push({
      t: "insight",
      text: worstT.label,
      tone: band(worstT.health, worstT.target),
      refs: [worstT.id],
      chat: `Walk me through ${worstT.label} — it's at ${worstT.health} against a target of ${worstT.target}. What should I do first?`,
    });
    segs.push(text(` — ${gap} under target${risk}.`));
    if (sugg) segs.push(text(` Fix: ${sugg.title}.`));
    const okCount = touches.length - belowT.length;
    if (okCount > 0)
      segs.push(
        text(
          ` The other ${okCount === 1 ? "signal is" : `${okCount} signals are`} on target.`,
        ),
      );
  } else {
    segs.push(
      text(
        ` Every signal here is on target${touches.length > 1 ? ` (${touches.length})` : ""}.`,
      ),
    );
  }

  return {
    segments: segs,
    pageChat: `Give me the read on ${g.label} — what's the most important thing to do here?`,
  };
}

/** One mode's rolled-up read, for the workspace (home) cross-mode synthesis. */
export type WorkspaceMode = {
  /** Mode slug — matches a `data-pulse-mode` tag on the home wall. */
  slug: string;
  label: string;
  overall: number | null;
  groups: PulseGroupDTO[];
};

/**
 * Build the WORKSPACE read — the home wall is a wall of MODES (Work · Customers ·
 * …), each a roll-up of its sections, so this reads across modes (the per-mode
 * `buildPulseSynthesis` reads across one mode's sections). Insight refs are mode
 * slugs (`data-pulse-mode`) or section keys (`data-pulse-cluster`) — both tagged
 * on the home wall. Returns null when no mode is instrumented.
 */
export function buildWorkspaceSynthesis(
  modes: WorkspaceMode[],
  opts: { subject: string },
): Synthesis {
  const subject = opts.subject.trim() || "The business";
  const lower = subject.toLowerCase();
  const live = modes.filter(
    (m): m is WorkspaceMode & { overall: number } =>
      m.overall != null && m.groups.length > 0,
  );
  if (live.length === 0) return null;

  const segs: SynthSeg[] = [];
  const overall = Math.round(
    live.reduce((a, m) => a + m.overall, 0) / live.length,
  );

  const { verb } = trajectory(
    live.flatMap((m) => m.groups.flatMap((g) => g.touches.filter(isLiveTouch))),
  );

  const sorted = [...live].sort((a, b) => a.overall - b.overall);
  const okModes = sorted.filter((m) => band(m.overall, 70) === "ok");
  const belowModes = sorted.filter((m) => band(m.overall, 70) !== "ok");
  const worst = belowModes[0] ?? null;

  // The single weakest section across the whole workspace — the one move.
  const action =
    live
      .flatMap((m) =>
        m.groups
          .filter(isLiveGroup)
          .filter((g) => g.touches.length > 0)
          .map((g) => ({ g, m })),
      )
      .filter((x) => x.g.health < 70)
      .sort((a, b) => a.g.health - b.g.health)[0] ?? null;

  // ── the number, and what's pulling it ──────────────────────────────────────
  segs.push(text(`${subject} is `));
  segs.push({
    t: "insight",
    text: `${verb} ${overall}`,
    tone: band(overall, 70),
    refs: ["all"],
    chat: `Give me the read across ${lower} — what's driving the overall ${overall}?`,
  });

  if (worst) {
    const atRisk = worst.groups.reduce(
      (n, g) =>
        n + g.touches.reduce((s, t) => s + (isLiveTouch(t) ? t.atRisk : 0), 0),
      0,
    );
    const stake =
      atRisk > 0
        ? `, ${atRisk} at risk`
        : belowModes.length > 1
          ? `, one of ${belowModes.length} areas under target`
          : "";
    segs.push(text(`, held down by `));
    segs.push({
      t: "insight",
      text: worst.label,
      tone: band(worst.overall, 70),
      refs: [worst.slug],
      chat: `Why is ${worst.label} dragging ${lower} down, and how do I fix it?`,
    });
    segs.push(text(` at ${worst.overall} against a 70 target${stake}.`));

    if (action) {
      const worstTouch = action.g.touches
        .filter(isLiveTouch)
        .filter((t) => t.health < t.target)
        .sort((a, b) => a.health - a.target - (b.health - b.target))[0];
      const gap = belowBy(action.g.health, 70);
      const sugg = worstTouch?.suggestions?.[0];
      segs.push(text(` Move first on `));
      segs.push({
        t: "insight",
        text: action.g.label,
        tone: band(action.g.health, 70),
        refs: [action.g.key],
        chat: `Walk me through ${action.g.label} in ${action.m.label} — what should I do first?`,
      });
      segs.push(text(` in ${action.m.label} — ${gap} under target.`));
      if (sugg) segs.push(text(` Fix: ${sugg.title}.`));
    }

    if (okModes.length > 0) {
      const best = [...okModes]
        .sort((a, b) => b.overall - a.overall)
        .slice(0, 2);
      const labels = joinNames(best.map((m) => m.label));
      segs.push(text(" "));
      segs.push({
        t: "insight",
        text: labels,
        tone: "ok",
        refs: best.map((m) => m.slug),
        chat: `${labels} look healthy — anything quietly at risk there?`,
      });
      segs.push(text(`${best.length > 1 ? " are" : " is"} carrying the rest.`));
    }
  } else {
    // ── all clear — quantify the calm (mirror of the mode read): the leaders at
    // their numbers, one grounded reassurance, the floor. Magnitudes, not filler.
    const best = [...okModes].sort((a, b) => b.overall - a.overall).slice(0, 2);
    segs.push(text(` — every area on target. `));
    const leader = best[0]!;
    segs.push({
      t: "insight",
      text: leader.label,
      tone: "ok",
      refs: [leader.slug],
      chat: `${leader.label} is leading at ${leader.overall} — anything quietly at risk there?`,
    });
    segs.push(text(` leads at ${leader.overall}`));
    if (best[1]) {
      segs.push(text(", "));
      segs.push({
        t: "insight",
        text: best[1].label,
        tone: "ok",
        refs: [best[1].slug],
        chat: `${best[1].label} looks healthy — anything quietly at risk there?`,
      });
      segs.push(text(` at ${best[1].overall}`));
    }
    const totalAtRisk = live.reduce(
      (n, m) =>
        n + m.groups.reduce((s, g) => s + g.touches.reduce((a, t) => a + (isLiveTouch(t) ? t.atRisk : 0), 0), 0),
      0,
    );
    segs.push(
      text(
        totalAtRisk === 0
          ? `. Nothing's at risk across ${live.length} area${live.length === 1 ? "" : "s"}`
          : `. ${totalAtRisk} signal${totalAtRisk === 1 ? "" : "s"} still to keep honest`,
      ),
    );
    const weakest = sorted[0]!;
    if (!best.some((b) => b.slug === weakest.slug)) {
      segs.push(text("; hold "));
      segs.push({
        t: "insight",
        text: weakest.label,
        tone: band(weakest.overall, 70),
        refs: [weakest.slug],
        chat: `${weakest.label} is the floor right now — is it trending the wrong way?`,
      });
      segs.push(text(` at ${weakest.overall}.`));
    } else {
      segs.push(text(", so hold the line."));
    }
  }

  return {
    segments: segs,
    pageChat: `Give me the read across ${lower} — what's the most important thing to do right now?`,
  };
}

// ── LLM read seam (docs/design/SYNTHESIS-READ-GENERATOR.md) ───────────────────
//
// The pure functions that let a Claude-generated read replace the templated body
// WITHOUT changing the block contract, at ANY altitude (workspace / mode / band):
// build the slim ALTITUDE-AGNOSTIC brief the generator consumes, hash it (the SWR
// + server cache key), and convert the returned prose back into interactive
// `SynthSeg[]` by linkifying the component/signal labels it names. The LLM owns
// the SENTENCE; this layer owns the wall-wiring (refs validated against the brief,
// so the LLM never invents an id). The two builders agree on subject/scope so the
// home's mode pre-warm and a mode page's own request hash IDENTICALLY (one gen).

/** The canonical sentence subject for a scope — shared by the read component AND
 *  the home pre-warm so their briefs (and hashes) match exactly. */
export function subjectForScope(scope: string): string {
  const head = scope.split(":")[0] ?? scope;
  const map: Record<string, string> = {
    work: "Work",
    customers: "Customer",
    product: "Product",
    operations: "Operations",
    agents: "Agents",
    workspace: "Business",
  };
  return map[head] ?? head.charAt(0).toUpperCase() + head.slice(1);
}

/** Slim one touch to the fields the read needs (and that hash stably). */
function slimTouch(t: PulseTouchDTO) {
  return {
    id: t.id,
    label: t.label,
    health: t.health ?? null,
    target: t.target,
    atRisk: t.atRisk,
    trend: t.trend ?? null,
    fix: t.suggestions?.[0]?.title ?? null,
  };
}

/**
 * Build the read brief for ONE scope (a mode root, or a band when `focus` is set).
 * `groups` are the scope's sections (each with its touchpoints). On a focused band
 * read the brief narrows to that one section and takes its label as the subject —
 * mirroring `buildBandRead`. Sections with no touches are dropped.
 */
export function buildModeReadBrief(
  groups: PulseGroupDTO[],
  overall: number | null,
  opts: { subject: string; scope: string; focus?: string | null },
): PulseSynthesisBrief {
  const live = groups.filter((g) => g.touches.length > 0);
  const focused = opts.focus ? live.find((g) => g.key === opts.focus) : null;
  const sel = focused ? [focused] : live;
  return {
    subject: (focused?.label ?? opts.subject).trim() || "This",
    scope: opts.scope,
    overall: overall ?? null,
    focus: opts.focus ?? null,
    groups: sel.map((g) => ({
      key: g.key,
      label: g.label,
      health: g.health ?? null,
      allUninstrumented: g.touches.every((t) => t.health == null),
      touches: g.touches.map(slimTouch),
    })),
  };
}

/**
 * Build the WORKSPACE read brief — the home wall is a wall of MODES, so each
 * `group` is a mode and its `touches` are that mode's SECTIONS (each section's
 * rollup carries target/atRisk). One altitude-agnostic brief, same shape as a
 * mode read, so the generator + linkifier are identical.
 */
export function buildWorkspaceReadBrief(
  modes: WorkspaceMode[],
  subject: string,
): PulseSynthesisBrief {
  const liveModes = modes
    .map((m) => ({ m, groups: m.groups.filter((g) => g.touches.length > 0) }))
    .filter(({ groups }) => groups.length > 0);
  return {
    subject: subject.trim() || "The business",
    scope: "workspace",
    overall: null,
    focus: null,
    groups: liveModes.map(({ m, groups }) => ({
      key: m.slug,
      label: m.label,
      health: m.overall ?? null,
      allUninstrumented: groups.every((g) => g.health == null),
      touches: groups.map((g) => {
        const t0 = g.touches[0];
        return {
          id: g.key,
          label: g.label,
          health: g.health ?? null,
          target: t0?.target ?? 70,
          atRisk: t0?.atRisk ?? 0,
          trend: t0?.trend ?? null,
          fix: t0?.suggestions?.[0]?.title ?? null,
        };
      }),
    })),
  };
}

/** Stable 32-bit FNV-1a hash of the brief (deterministic key order from the
 *  builders above), base-36. Keys the SWR fetch + the server-side read cache —
 *  the read regenerates only when the pulse materially changes. */
export function hashBrief(brief: PulseSynthesisBrief): string {
  const s = JSON.stringify(brief);
  let h = 2166136261;
  for (let i = 0; i < s.length; i++) {
    h ^= s.charCodeAt(i);
    h = Math.imul(h, 16777619);
  }
  return (h >>> 0).toString(36);
}

const escapeRe = (s: string) => s.replace(/[.*+?^${}()|[\]\\/]/g, "\\$&");

/**
 * Convert an LLM-generated read (prose) into the interactive `SynthSeg[]` the
 * block renders. Component/signal labels the read names become `insight` segments
 * that light the matching wall bar (`data-pulse-mode` / `data-pulse-cluster` /
 * `data-pulse-bar`) on hover and seed the chat on click — but ONLY labels that are
 * unambiguous in the brief (a label appearing twice, e.g. "Goals" in Work + Agents
 * on the home wall, would light the wrong bar, so it stays plain text). The LLM
 * never supplies a ref id; every ref is a real slug/key resolved here. Returns
 * null on an empty read.
 */
export function llmReadToSynthesis(
  read: string,
  brief: PulseSynthesisBrief,
  pageChat: string,
): Synthesis {
  const trimmed = read.trim();
  if (!trimmed) return null;

  // Index labels → their (single) wall target: components first, then signals.
  type Entry = { label: string; ref: string; tone: Band; chat: string; count: number };
  const byLabel = new Map<string, Entry>();
  const add = (label: string, ref: string, tone: Band, chat: string) => {
    const key = label.trim().toLowerCase();
    if (!key) return;
    const hit = byLabel.get(key);
    if (hit) hit.count++;
    else byLabel.set(key, { label: label.trim(), ref, tone, chat, count: 1 });
  };
  for (const g of brief.groups) {
    const gt: Band = g.health != null ? band(g.health, 70) : "risk";
    add(g.label, g.key, gt, `Walk me through ${g.label} — what should I do first?`);
    for (const t of g.touches) {
      // Skip a rollup touch that just mirrors its parent (same label) — at
      // section-rollup altitude every section has one touch named like the
      // section, and double-counting would mark the label ambiguous. A genuine
      // sub-signal (distinct label) is still indexed → linkable to its own bar.
      if (t.label.trim().toLowerCase() === g.label.trim().toLowerCase()) continue;
      const tt: Band = t.health != null ? band(t.health, t.target) : "risk";
      add(t.label, t.id, tt, `Walk me through ${t.label} — what should I do first?`);
    }
  }

  // Only unambiguous labels are linkable (a duplicate would light the wrong bar).
  const targets = [...byLabel.values()]
    .filter((e) => e.count === 1)
    .sort((a, b) => b.label.length - a.label.length); // longest-first
  if (targets.length === 0) {
    return { segments: [text(trimmed)], pageChat };
  }

  const rx = new RegExp(`(${targets.map((t) => escapeRe(t.label)).join("|")})`, "gi");
  const used = new Set<string>();
  const segs: SynthSeg[] = [];
  let last = 0;
  let mt: RegExpExecArray | null;
  while ((mt = rx.exec(trimmed)) !== null) {
    const matched = mt[0];
    const key = matched.toLowerCase();
    const entry = byLabel.get(key);
    // Skip ambiguous (count>1, never in `targets`) or already-linked labels; the
    // text stays in the next plain run since `last` is not advanced past it.
    if (!entry || entry.count !== 1 || used.has(key)) continue;
    if (mt.index > last) segs.push(text(trimmed.slice(last, mt.index)));
    used.add(key);
    segs.push({ t: "insight", text: matched, tone: entry.tone, refs: [entry.ref], chat: entry.chat });
    last = mt.index + matched.length;
  }
  if (last < trimmed.length) segs.push(text(trimmed.slice(last)));
  return { segments: segs, pageChat };
}
